/**
 * API Endpoints organized by module
 *
 * Usage Status Legend:
 * - [ACTIVE] - Endpoint is actively used in the frontend
 * - [PLANNED] - Endpoint exists in backend but not yet integrated in frontend
 * - [DEPRECATED] - Endpoint marked for removal
 *
 * Actively Used Endpoints:
 * - Fleet: FLEET_ASSET_SUMMARIES, FLEET_HEALTH
 * - Comparison: COMPARE_KPIS, COMPARE_KPIS_TRENDS, COMPARE_KPIS_CORRELATIONS
 * - Dashboard: Most DASHBOARD_* endpoints via dashboardApi
 * - Models: MODELS_VERSIONS, MODELS_METRICS, MODELS_FEATURES
 *
 * Planned/Reserved Endpoints (not yet integrated):
 * - Authentication: AUTH_* - Login/logout not implemented in UI
 * - Data Upload: DATA_UPLOAD, DATA_QUALITY
 * - Reports Export: REPORTS_* - Export functionality planned
 * - Admin: ADMIN_* - Admin panel planned
 * - WebSocket: WS_* - Real-time updates planned
 * - Alerts Management: ALERTS_RULES, ALERTS_HISTORY
 */
export const ENDPOINTS = {
  // Health
  HEALTH: '/health',
  HEALTH_LIVE: '/health/live',
  HEALTH_READY: '/health/ready',
  HEALTH_MODELS: '/health/models',
  HEALTH_DRIFT: (assetId: string) => `/health/drift/${assetId}`,
  HEALTH_BRAIN: '/health/brain',
  HEALTH_BRAIN_MEMORY: '/health/brain/memory',
  HEALTH_BRAIN_INDUSTRY: (industryId: string) => `/health/brain/${industryId}`,
  HEALTH_BRAIN_HEALING: '/health/brain/healing-history',
  HEALTH_BRAIN_FLEET: '/federated/intelligence/brain-health-fleet',
  HEALTH_DEPLOYMENT_MODE: '/health/deployment-mode',

  // Continual Learning — Brain Dashboard
  CONTINUAL_DASHBOARD: '/continual/dashboard',
  CONTINUAL_DASHBOARD_INDUSTRY: (industryId: string) => `/continual/dashboard/${industryId}`,

  // Authentication
  AUTH_LOGIN: '/auth/login',
  AUTH_LOGOUT: '/auth/logout',
  AUTH_ME: '/auth/me',
  AUTH_VERIFY: '/auth/verify',
  AUTH_REFRESH: '/auth/refresh',
  AUTH_VALIDATE: '/auth/validate',

  // Predictions
  PREDICT: '/predict',
  PREDICT_ASSET: (assetId: string) => `/predict/${assetId}`,
  PREDICT_SINGLE: (assetId: string) => `/predict/${assetId}/single`,
  PREDICT_UNIFIED: (assetId: string) => `/predict/unified/${assetId}`,
  PREDICT_EXPLANATIONS: '/predict/explanations',
  PREDICT_FEEDBACK: '/predict/feedback',
  PREDICT_FEEDBACK_QUEUE: '/predict/feedback/queue',
  PREDICT_FORECAST: (assetId: string) => `/forecast/${assetId}`,
  PREDICT_FORECAST_FOUNDATION: (assetId: string) => `/forecast/foundation/${assetId}`,
  PREDICT_BATCH: '/predict/batch',

  // SHAP Explanations
  EXPLAIN: (assetId: string) => `/explain/${assetId}`,
  EXPLAIN_SUMMARY: (assetId: string) => `/explain/${assetId}/summary`,
  EXPLAIN_SHAP: (assetId: string) => `/explain/${assetId}/shap`,
  EXPLAIN_FEATURE_IMPORTANCE: (assetId: string) => `/explain/${assetId}/feature-importance`,

  // Dashboard
  DASHBOARD: (assetId: string) => `/dashboard/${assetId}`,
  DASHBOARD_COMPARE: '/dashboard/compare',
  DASHBOARD_METRICS: (assetId: string) => `/dashboard/${assetId}/metrics`,
  DASHBOARD_KPIS: (assetId: string) => `/dashboard/${assetId}/kpis`,
  DASHBOARD_OVERVIEW: '/dashboard/overview',
  DASHBOARD_SENSOR_TRENDS: (assetId: string) => `/dashboard/sensor-trends/${assetId}`,
  DASHBOARD_ANOMALY_TIMELINE: (assetId: string) => `/dashboard/anomaly-timeline/${assetId}`,
  DASHBOARD_DATA_QUALITY: (assetId: string) => `/dashboard/data-quality-pie/${assetId}`,
  DASHBOARD_FAULT_COUNTS: '/dashboard/fault-counts-by-asset',
  DASHBOARD_FAULT_SEVERITY: (assetId: string) => `/dashboard/fault-severity/${assetId}`,
  DASHBOARD_RESOLUTION_TIME: (assetId: string) => `/dashboard/resolution-time/${assetId}`,
  DASHBOARD_GAS_COMPOSITION: (assetId: string) => `/dashboard/gas-composition/${assetId}`,
  DASHBOARD_VCP_SETTINGS: (assetId: string) => `/dashboard/vcp-settings/${assetId}`,
  DASHBOARD_CORRELATIONS: (assetId: string) => `/dashboard/correlations/${assetId}`,
  DASHBOARD_KNOWLEDGE_GRAPH: (assetId: string) => `/dashboard/knowledge-graph/${assetId}`,
  DASHBOARD_FAILURE_PREDICTIONS: (assetId: string) => `/dashboard/failure-predictions/${assetId}`,
  DASHBOARD_SME_INSIGHTS: (assetId: string) => `/dashboard/sme-insights/${assetId}`,
  DASHBOARD_AI_INSIGHTS: (assetId: string) => `/dashboard/ai-insights/${assetId}`,
  DASHBOARD_MAINTENANCE_SCHEDULER: (assetId: string) => `/dashboard/maintenance-scheduler/${assetId}`,
  DASHBOARD_ACTION_QUEUE: '/dashboard/action-queue',
  DASHBOARD_ACTION_SCHEDULE: (actionId: string) => `/dashboard/action-queue/${actionId}/schedule`,
  DASHBOARD_ACTION_COMPLETE: (actionId: string) => `/dashboard/action-queue/${actionId}/complete`,
  DASHBOARD_EXECUTIVE_SUMMARY: (assetId: string) => `/dashboard/executive-summary/${assetId}`,
  DASHBOARD_EXECUTIVE_SUMMARY_MULTI: '/dashboard/executive-summary-multi',
  DASHBOARD_STATISTICAL_SUMMARY: (assetId: string) => `/dashboard/statistical-summary/${assetId}`,
  DASHBOARD_DRILL_DOWN: (assetId: string) => `/dashboard/drill-down/${assetId}`,
  DASHBOARD_MODEL_PERFORMANCE: (assetId: string) => `/dashboard/model-performance/${assetId}`,
  DASHBOARD_FLEET_FORECAST: '/dashboard/fleet-forecast',
  DASHBOARD_FLEET_MODEL_METRICS: '/dashboard/fleet-model-metrics',
  DASHBOARD_UPCOMING_EVENTS: '/dashboard/upcoming-events',
  DASHBOARD_WHAT_IF_SCENARIOS: '/dashboard/what-if-scenarios',
  DASHBOARD_HISTORICAL_PATTERNS: (assetId: string) => `/dashboard/historical-patterns/${assetId}`,
  DASHBOARD_MAINTENANCE_EFFICIENCY: '/dashboard/maintenance-efficiency',
  DASHBOARD_EXECUTIVE_TRENDS: '/dashboard/executive-trends',
  DASHBOARD_MODEL_ACCURACY_HISTORY: '/dashboard/model-accuracy-history',
  DASHBOARD_PARTS_PREDICTION: '/dashboard/parts-prediction',
  DASHBOARD_OKR_OBJECTIVES: '/dashboard/okr-objectives',
  DASHBOARD_MAINTENANCE_ACTIONS: '/dashboard/maintenance-actions',
  DASHBOARD_COST_BREAKDOWN: '/dashboard/cost-breakdown',
  DASHBOARD_ASSET_TYPE: (assetId: string) => `/dashboard/asset-type/${assetId}`,

  // Data
  DATA: '/data',
  DATA_ANOMALIES: (assetId: string) => `/data/${assetId}/anomalies`,
  DATA_QUERY: (assetId: string) => `/data/${assetId}/query`,
  DATA_UPLOAD: '/data/upload',
  DATA_AVAILABLE_ASSETS: '/data/assets',
  DATA_QUALITY: '/data/quality',

  // Reports
  REPORTS_GENERATE: (assetId: string) => `/reports/${assetId}/generate`,
  REPORTS_GET: (jobId: string) => `/reports/${jobId}`,
  REPORTS_HTML: (assetId: string) => `/reports/${assetId}/html`,
  REPORTS_MARKDOWN: (assetId: string) => `/reports/${assetId}/markdown`,
  REPORTS_JSON: (assetId: string) => `/reports/${assetId}/json`,
  REPORTS_EXECUTIVE_SUMMARY: (assetId: string) => `/reports/${assetId}/executive_summary`,
  REPORTS_TREND_ANALYSIS: (assetId: string) => `/reports/trend-analysis/${assetId}`,

  // Models
  MODELS: '/models',
  MODELS_STATUS: '/models/status',
  MODELS_SUMMARY: '/models/summary',
  MODELS_ASSETS: '/models/assets',
  MODELS_VALIDATE: '/models/validate',
  MODELS_RELOAD: '/models/reload',
  MODELS_ASSET: (assetId: string) => `/models/${assetId}`,
  MODELS_DEPLOY: (assetId: string) => `/models/${assetId}/deploy`,
  MODELS_VERSIONS: (assetId: string) => `/models/${assetId}/versions`,
  MODELS_METRICS: (assetId: string) => `/models/${assetId}/metrics`,
  MODELS_FEATURES: (assetId: string) => `/models/${assetId}/features`,
  MODELS_EXPORT: (assetId: string) => `/models/${assetId}/export`,
  MODELS_COMPARE: '/models/compare',

  // Alerts
  ALERTS: '/alerts',
  ALERTS_GET: (alertId: string) => `/alerts/${alertId}`,
  ALERTS_SUMMARY: '/alerts/summary',
  ALERTS_STATS: '/alerts/stats',
  ALERTS_RULES: '/alerts/rules',
  ALERTS_RULE: (ruleId: string) => `/alerts/rules/${ruleId}`,
  ALERTS_HISTORY: (assetId: string) => `/alerts/${assetId}/history`,
  ALERTS_ACKNOWLEDGE: (alertId: string) => `/alerts/${alertId}/acknowledge`,
  ALERTS_RESOLVE: (alertId: string) => `/alerts/${alertId}/resolve`,
  ALERTS_FROM_PREDICTION: '/alerts/from_prediction',

  // Comparison
  COMPARE_KPIS: '/compare/kpis',
  COMPARE_KPIS_ADVANCED: '/compare/kpis-advanced',
  COMPARE_KPIS_TRENDS: '/compare/kpis-trends',
  COMPARE_KPIS_CORRELATIONS: '/compare/kpis-correlations',
  COMPARE_TARGET_CATEGORIES: '/compare/target-categories',
  COMPARE_VISUALS: (vizType: string) => `/compare/visuals/${vizType}`,
  COMPARE_GAS_COMPOSITION: '/compare/gas-composition',
  COMPARE_VCP_SETTINGS: '/compare/vcp-settings',
  COMPARE_SENSOR_TRENDS: '/compare/sensor-trends',
  COMPARE_ANOMALY_TIMELINES: '/compare/anomaly-timelines',
  COMPARE_EXECUTIVE_SUMMARIES: '/compare/executive-summaries',
  COMPARE_EXPORT_REPORTS: '/compare/export-reports',

  // Fleet (aggregated fleet-level endpoints)
  FLEET_HEALTH: '/fleet/health',
  FLEET_ASSET_SUMMARIES: '/fleet/asset-summaries',
  FLEET_INSIGHTS: '/fleet/insights',
  FLEET_MODEL_PERFORMANCE: '/fleet/model-performance',
  FLEET_METRICS: '/fleet/metrics',
  FLEET_OVERVIEW: '/fleet/overview',

  // WebSocket
  WS_PREDICT: (assetId: string) => `/ws/predict/${assetId}`,
  WS_STREAM: (assetId: string) => `/ws/stream/${assetId}`,
  WS_ALERTS: '/ws/alerts',
  WS_HEALTH: '/ws/health',
  WS_STATUS: '/ws/status',

  // Admin
  ADMIN_STATUS: '/admin/status',
  ADMIN_ASSETS: '/admin/assets',
  ADMIN_STATS: '/admin/stats',
  ADMIN_CONFIG: '/admin/config',
  ADMIN_LOGS: '/admin/logs',
  ADMIN_REGISTRY: '/admin/registry',
  ADMIN_RELOAD_MODELS: '/admin/models/reload',
  ADMIN_CACHE_CLEAR: '/admin/cache',
  ADMIN_SYSTEM_INFO: '/admin/system-info',

  // Metrics
  METRICS: '/metrics',

  // Business Value & ROI
  BUSINESS_VALUE_CALCULATE: '/business-value/calculate-value',
  BUSINESS_VALUE_BENCHMARKS: (industryId: string) => `/business-value/benchmarks/${industryId}`,
  BUSINESS_VALUE_KPIS: (industryId: string) => `/business-value/kpis/${industryId}`,
  BUSINESS_VALUE_KPIS_SNAPSHOT: (industryId: string) => `/business-value/kpis/${industryId}/snapshot`,
  BUSINESS_VALUE_ROI_SUMMARY: (industryId: string) => `/business-value/roi-summary/${industryId}`,
  BUSINESS_VALUE_SAVINGS_RECORD: '/business-value/savings/record',
  BUSINESS_VALUE_SAVINGS_HISTORY: (industryId: string) => `/business-value/savings/history/${industryId}`,

  // Workflows
  WORKFLOWS: '/workflows',
  WORKFLOW: (workflowId: string) => `/workflows/${workflowId}`,
  WORKFLOW_EXECUTE: (workflowId: string) => `/workflows/${workflowId}/execute`,
  WORKFLOW_EXECUTIONS: '/workflows/executions',
  WORKFLOW_EXECUTION: (executionId: string) => `/workflows/executions/${executionId}`,
  WORKFLOW_EXECUTION_STEP: (executionId: string, stepId: string) => `/workflows/executions/${executionId}/steps/${stepId}`,
  WORKFLOW_EXECUTION_PAUSE: (executionId: string) => `/workflows/executions/${executionId}/pause`,
  WORKFLOW_EXECUTION_RESUME: (executionId: string) => `/workflows/executions/${executionId}/resume`,
  WORKFLOW_EXECUTION_CANCEL: (executionId: string) => `/workflows/executions/${executionId}/cancel`,

  // Decision Intelligence
  DECISIONS: '/decisions',
  DECISION: (decisionId: string) => `/decisions/${decisionId}`,
  DECISION_ACTION: (decisionId: string) => `/decisions/${decisionId}/action`,
  DECISION_FEEDBACK: (decisionId: string) => `/decisions/${decisionId}/feedback`,
  DECISIONS_BY_ROLE: (role: string) => `/decisions/by-role/${role}`,
  DECISIONS_GENERATE: '/decisions/generate',

  // Training Jobs [ACTIVE] - Model training and retraining pipeline
  TRAINING_JOBS: '/training/jobs',
  TRAINING_JOB: (jobId: string) => `/training/jobs/${jobId}`,
  TRAINING_JOB_RETRY: (jobId: string) => `/training/jobs/${jobId}/retry`,
  TRAINING_JOB_PROGRESS: (jobId: string) => `/training/jobs/${jobId}/progress`,
  TRAINING_LIMITS: '/training/limits',
  TRAINING_STATS: '/training/stats',
  TRAINING_START: '/training/start',
  TRAINING_STOP: '/training/stop',

  // Training Schedules [ACTIVE] - Scheduled/recurring training
  TRAINING_SCHEDULES: '/training/schedules',
  TRAINING_SCHEDULE: (scheduleId: string) => `/training/schedules/${scheduleId}`,
  TRAINING_SCHEDULE_PAUSE: (scheduleId: string) => `/training/schedules/${scheduleId}/pause`,
  TRAINING_SCHEDULE_RESUME: (scheduleId: string) => `/training/schedules/${scheduleId}/resume`,

  // Training Workers [ACTIVE] - GPU worker pool management
  TRAINING_WORKERS: '/training/workers',
  TRAINING_WORKER: (workerId: string) => `/training/workers/${workerId}`,
  TRAINING_WORKER_HEARTBEAT: (workerId: string) => `/training/workers/${workerId}/heartbeat`,

  // Convenience Training Endpoints [ACTIVE]
  TRAINING_TRAIN_ASSET: (assetId: string) => `/training/train/asset/${assetId}`,
  TRAINING_TRAIN_FLEET: '/training/train/fleet',
  TRAINING_TRAIN_BATCH: '/training/train/batch',

  // Training Queue [ACTIVE] - Persistent job queue management
  TRAINING_QUEUE: '/training/queue',
  TRAINING_QUEUE_CANCEL: (jobId: string) => `/training/queue/${jobId}`,
  TRAINING_QUEUE_RETRY: (jobId: string) => `/training/queue/${jobId}/retry`,

  // Token Usage [ACTIVE] - Token consumption tracking
  TOKEN_USAGE_CURRENT: '/subscriptions/usage/current',
  TOKEN_USAGE_HISTORY: '/subscriptions/usage/history',

  // Subscription [ACTIVE] - Subscription management
  SUBSCRIPTION_SUMMARY: '/subscriptions/summary',
  SUBSCRIPTION_USAGE: '/subscriptions/usage',
  SUBSCRIPTION_EXTEND_GRACE: (subId: string) => `/subscriptions/${subId}/extend-grace`,
  SUBSCRIPTION_TRANSITION_MODEL: (subId: string) => `/subscriptions/${subId}/transition-model-size`,

  // Knowledge Base [ACTIVE] - Industry knowledge base endpoints
  KNOWLEDGE_WORKFLOWS: (industryId: string) => `/industry/knowledge/workflows/${industryId}`,
  KNOWLEDGE_WORKFLOW_DETAIL: (industryId: string, workflowId: string) => `/industry/knowledge/workflows/${industryId}/${workflowId}`,
  KNOWLEDGE_TROUBLESHOOTING: (industryId: string) => `/industry/knowledge/troubleshooting/${industryId}`,
  KNOWLEDGE_TROUBLESHOOTING_DETAIL: (industryId: string, guideId: string) => `/industry/knowledge/troubleshooting/${industryId}/${guideId}`,
  KNOWLEDGE_SENSORS: (industryId: string, assetType: string) => `/industry/knowledge/sensors/${industryId}/${assetType}`,
  KNOWLEDGE_KPIS: (industryId: string, domainId: string) => `/industry/knowledge/kpis/${industryId}/${domainId}`,
  KNOWLEDGE_COMPLIANCE: (industryId: string, standardId: string) => `/industry/knowledge/compliance/${industryId}/${standardId}`,
  // Narratives [ACTIVE] - AI narrative generation
  NARRATIVES_GENERATE: '/narratives/generate',

  // AGI / AGEI Intelligence [ACTIVE] - AI reasoning, planning, LLM chat
  AGI_QUERY: '/agi/query',
  AGI_PLAN: '/agi/plan',
  AGI_INSIGHTS: '/agi/insights',
  AGI_LEARN: '/agi/learn',
  AGI_LLM_STATUS: '/agi/llm/status',
  AGI_EXPLAIN_ANOMALY: '/agi/explain-anomaly',
  AGI_REPORT: '/agi/report',

  // Simulation / Digital Twin / Generative Design [ACTIVE]
  SIMULATION_RUN: '/features/simulation/run',
  SIMULATION_VARIABLES: '/features/simulation/variables',
  SIMULATION_SCENARIOS: '/features/simulation/scenarios',
  SIMULATION_GENERATE_DESIGNS: '/features/simulation/generate-designs',
  SIMULATION_EVALUATE_DESIGN: '/features/simulation/evaluate-design',
  SIMULATION_OPTIMIZE: '/features/simulation/optimize',
  // Physics Engine [ACTIVE] - Physics-based ODE simulation
  SIMULATION_PHYSICS_RUN: '/features/simulation/physics/run',
  SIMULATION_PHYSICS_OPTIMIZE: '/features/simulation/physics/optimize',
  SIMULATION_PHYSICS_MODELS: '/features/simulation/physics/models',
  SIMULATION_PHYSICS_INSIGHTS: '/features/simulation/physics/insights',

  // Maintenance & Work Orders [ACTIVE] - Maintenance scheduling and work order management
  MAINTENANCE_SCHEDULE: '/maintenance/schedule',
  MAINTENANCE_SCHEDULE_ASSET: (assetId: string) => `/maintenance/schedule/${assetId}`,
  MAINTENANCE_SCHEDULE_STATUS: (taskId: string) => `/maintenance/schedule/${taskId}/status`,
  MAINTENANCE_SCHEDULE_CANCEL: (taskId: string) => `/maintenance/schedule/${taskId}`,
  MAINTENANCE_RECOMMENDATIONS: '/maintenance/recommendations',
  MAINTENANCE_WORK_ORDERS: '/maintenance/work-orders',
  MAINTENANCE_WORK_ORDERS_ASSET: (assetId: string) => `/maintenance/work-orders/${assetId}`,
  MAINTENANCE_WORK_ORDER_STATUS: (workOrderId: string) => `/maintenance/work-orders/${workOrderId}/status`,
  MAINTENANCE_OPTIMIZE_RESOURCES: '/maintenance/optimize-resources',
  // Real per-asset Weibull RUL. Returns available:false + a machine-readable
  // reason when an input is genuinely missing — never a synthesized estimate.
  MAINTENANCE_ASSET_RUL: (assetId: string) => `/maintenance/assets/${assetId}/rul`,

  // Parts Catalog [ACTIVE] - Parts failure prediction and catalog management
  PARTS_BY_ASSET_TYPE: (industry: string, assetType: string) => `/parts/${industry}/${assetType}`,
  PARTS_DETAIL: (industry: string, assetType: string, partId: string) => `/parts/${industry}/${assetType}/${partId}`,
  PARTS_PREDICT_FAILURE: '/parts/predict/failure',
  PARTS_PREDICT_MAINTENANCE: '/parts/predict/maintenance',
  PARTS_COST_CALCULATE: '/parts/cost/calculate',
  PARTS_CRITICAL: (industry: string, assetType: string) => `/parts/${industry}/${assetType}/critical`,
  PARTS_ANNUAL_COST: (industry: string, assetType: string) => `/parts/${industry}/${assetType}/annual-cost`,
  PARTS_REPLACEMENT_NEEDED: '/parts/replacement/needed',
  PARTS_SEARCH: '/parts/search',

  // Workflow Execution [ACTIVE] - Workflow execution and checklist management
  WORKFLOW_EXECUTION_START: '/workflow-executions/start',
  WORKFLOW_EXECUTION_STEP_UPDATE: (execId: string, stepId: string) => `/workflow-executions/${execId}/steps/${stepId}`,

  // Maintenance Intelligence [ACTIVE] - Maintenance history, failure patterns, inventory
  MAINTENANCE_HISTORY: (assetId: string) => `/maintenance/history/${assetId}`,
  MAINTENANCE_HISTORY_PROFILE: (assetId: string) => `/maintenance/history/${assetId}/profile`,
  INVENTORY_STATUS: (facilityId: string) => `/inventory/${facilityId}`,
  INVENTORY_LOW_STOCK: (facilityId: string) => `/inventory/${facilityId}/low-stock`,

  // Audit [ACTIVE] - User-level audit logging and action history
  AUDIT_USER_LOG: '/audit/user-log',
  AUDIT_USER_HISTORY: '/audit/user-history',
} as const
